import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Legend,
  Tooltip,
} from "chart.js";
import DataTable from "react-data-table-component";
import {
  FiUsers,
  FiUserCheck,
  FiCopy,
  FiClock,
  FiStar,
  FiAward,
  FiCreditCard,
  FiRefreshCw,
  FiUserPlus,
} from "react-icons/fi";
import {
  fetchInviteOverview,
  fetchInviters,
  fetchInvites,
  setCanInvite,
} from "../Service/referralApi";
import "./InviteAnalytics.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Legend,
  Tooltip
);

const toYmd = (d) => d.toISOString().split("T")[0];

const defaultEnd = () => toYmd(new Date());
const defaultStart = () => toYmd(new Date(Date.now() - 29 * 86400000));

const formatNum = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString("en-IN");
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const STATUS_FILTERS = [
  { value: "", label: "All statuses" },
  { value: "registered", label: "Registered" },
  { value: "paid_member", label: "Paid Member" },
  { value: "duplicate", label: "Duplicate" },
  { value: "pending_otp", label: "Pending OTP" },
];

const statusClass = (status) => {
  const s = (status || "").toLowerCase();
  if (s.includes("pending")) return "pending";
  if (s.includes("paid")) return "paid";
  if (s.includes("duplicate")) return "duplicate";
  if (s.includes("registered")) return "registered";
  return "registered";
};

const SummaryCard = ({ label, value, hint, icon: Icon, tone }) => (
  <div className={`invite-kpi invite-kpi--${tone || "brand"}`}>
    <div className="invite-kpi-top">
      <span className="invite-kpi-label">{label}</span>
      {Icon ? (
        <span className="invite-kpi-icon">
          <Icon />
        </span>
      ) : null}
    </div>
    <div className="invite-kpi-value">{formatNum(value)}</div>
    {hint ? <div className="invite-kpi-hint">{hint}</div> : null}
  </div>
);

const InviteAnalytics = () => {
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);

  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewError, setOverviewError] = useState("");

  const [activeTab, setActiveTab] = useState("inviters");

  const [inviters, setInviters] = useState([]);
  const [invitersCount, setInvitersCount] = useState(0);
  const [invitersPage, setInvitersPage] = useState(1);
  const [invitersPageSize, setInvitersPageSize] = useState(20);
  const [invitersLoading, setInvitersLoading] = useState(false);
  const [inviterSearchInput, setInviterSearchInput] = useState("");
  const [inviterSearch, setInviterSearch] = useState("");
  const [togglingInvite, setTogglingInvite] = useState(null);

  const [invites, setInvites] = useState([]);
  const [invitesCount, setInvitesCount] = useState(0);
  const [invitesPage, setInvitesPage] = useState(1);
  const [invitesPageSize, setInvitesPageSize] = useState(20);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [inviteSearchInput, setInviteSearchInput] = useState("");
  const [inviteSearch, setInviteSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [isProFilter, setIsProFilter] = useState("");
  const [otpFilter, setOtpFilter] = useState("");
  const [inviterHccFilter, setInviterHccFilter] = useState("");

  const dateParams = useMemo(
    () => ({ start_date: startDate, end_date: endDate }),
    [startDate, endDate]
  );

  const loadOverview = useCallback(async () => {
    setOverviewLoading(true);
    setOverviewError("");
    try {
      const res = await fetchInviteOverview(dateParams);
      setOverview(res.data);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (err?.response?.status === 403
          ? "You do not have permission to view invite analytics."
          : "Failed to load overview.");
      setOverviewError(detail);
      setOverview(null);
    } finally {
      setOverviewLoading(false);
    }
  }, [dateParams]);

  const loadInviters = useCallback(async () => {
    setInvitersLoading(true);
    try {
      const params = {
        ...dateParams,
        page: invitersPage,
        page_size: invitersPageSize,
      };
      if (inviterSearch) params.search = inviterSearch;
      const res = await fetchInviters(params);
      setInviters(res.data.results || []);
      setInvitersCount(res.data.count || 0);
    } catch (err) {
      console.error(err);
      setInviters([]);
      setInvitersCount(0);
    } finally {
      setInvitersLoading(false);
    }
  }, [dateParams, invitersPage, invitersPageSize, inviterSearch]);

  const loadInvites = useCallback(async () => {
    setInvitesLoading(true);
    try {
      const params = {
        ...dateParams,
        page: invitesPage,
        page_size: invitesPageSize,
      };
      if (inviteSearch) params.search = inviteSearch;
      if (statusFilter) params.status = statusFilter;
      if (isProFilter !== "") params.is_pro = isProFilter;
      if (otpFilter !== "") params.is_mobile_verified = otpFilter;
      if (inviterHccFilter) params.inviter_hcc_id = inviterHccFilter;

      const res = await fetchInvites(params);
      setInvites(res.data.results || []);
      setInvitesCount(res.data.count || 0);
    } catch (err) {
      console.error(err);
      setInvites([]);
      setInvitesCount(0);
    } finally {
      setInvitesLoading(false);
    }
  }, [
    dateParams,
    invitesPage,
    invitesPageSize,
    inviteSearch,
    statusFilter,
    isProFilter,
    otpFilter,
    inviterHccFilter,
  ]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    if (activeTab === "inviters") loadInviters();
  }, [activeTab, loadInviters]);

  useEffect(() => {
    if (activeTab === "invites") loadInvites();
  }, [activeTab, loadInvites]);

  const refreshAll = () => {
    loadOverview();
    if (activeTab === "inviters") loadInviters();
    else loadInvites();
  };

  const handleToggleCanInvite = async (row) => {
    const key = row.profile_id || row.hcc_id;
    setTogglingInvite(key);
    try {
      const res = await setCanInvite({
        hcc_id: row.hcc_id,
        profile_id: row.profile_id,
        can_invite: !row.can_invite,
      });
      const updated = res.data;
      setInviters((prev) =>
        prev.map((item) =>
          item.profile_id === updated.profile_id || item.hcc_id === updated.hcc_id
            ? { ...item, can_invite: updated.can_invite }
            : item
        )
      );
    } catch (err) {
      alert(
        err?.response?.data?.detail ||
          "Failed to update invite permission."
      );
    } finally {
      setTogglingInvite(null);
    }
  };

  const openInvitesForInviter = (row) => {
    setInviterHccFilter(row.hcc_id || "");
    setInviteSearchInput("");
    setInviteSearch("");
    setInvitesPage(1);
    setActiveTab("invites");
  };

  const summary = overview?.summary || {};

  const chartData = useMemo(() => {
    const daily = overview?.daily || [];
    return {
      labels: daily.map((d) => d.date),
      datasets: [
        {
          label: "Joined",
          data: daily.map((d) => d.joined ?? 0),
          borderColor: "#a72b89",
          backgroundColor: "rgba(167, 43, 137, 0.12)",
          tension: 0.3,
          fill: false,
        },
        {
          label: "Unique",
          data: daily.map((d) => d.unique ?? 0),
          borderColor: "#0ea5e9",
          backgroundColor: "rgba(14, 165, 233, 0.12)",
          tension: 0.3,
          fill: false,
        },
        {
          label: "Duplicates",
          data: daily.map((d) => d.duplicates ?? 0),
          borderColor: "#9ca3af",
          backgroundColor: "rgba(156, 163, 175, 0.12)",
          tension: 0.3,
          fill: false,
        },
        {
          label: "Pro",
          data: daily.map((d) => d.pro ?? 0),
          borderColor: "#10b981",
          backgroundColor: "rgba(16, 185, 129, 0.12)",
          tension: 0.3,
          fill: false,
        },
      ],
    };
  }, [overview]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: "bottom" },
        tooltip: { mode: "index", intersect: false },
      },
      scales: {
        x: { grid: { display: false } },
        y: { beginAtZero: true, ticks: { precision: 0 } },
      },
      interaction: { mode: "nearest", axis: "x", intersect: false },
    }),
    []
  );

  const tableStyles = {
    cells: { style: { justifyContent: "center" } },
    headCells: {
      style: {
        justifyContent: "center",
        color: "rgb(33,37,41)",
        fontSize: "12px",
        fontWeight: 500,
      },
    },
  };

  const inviterColumns = [
    {
      name: "Inviter",
      minWidth: "180px",
      cell: (row) => (
        <div className="invite-cell-left">
          <b>{row.hcc_id || "—"}</b>
          <div>{row.name || "—"}</div>
          <div className="invite-muted">{row.email || ""}</div>
        </div>
      ),
    },
    {
      name: "Joined",
      selector: (row) => row.total_joined,
      cell: (row) => formatNum(row.total_joined),
      width: "90px",
    },
    {
      name: "Unique",
      selector: (row) => row.unique_signups,
      cell: (row) => formatNum(row.unique_signups),
      width: "90px",
    },
    {
      name: "Duplicates",
      selector: (row) => row.duplicates,
      cell: (row) => formatNum(row.duplicates),
      width: "100px",
    },
    {
      name: "Pro",
      selector: (row) => row.pro_from_invite,
      cell: (row) => formatNum(row.pro_from_invite),
      width: "80px",
    },
    {
      name: "Premium",
      selector: (row) => row.premium_conversions,
      cell: (row) => formatNum(row.premium_conversions),
      width: "90px",
    },
    {
      name: "Credits",
      selector: (row) => row.credits_earned,
      cell: (row) => formatNum(row.credits_earned),
      width: "90px",
    },
    {
      name: "Can invite",
      width: "140px",
      cell: (row) => {
        const key = row.profile_id || row.hcc_id;
        const busy = togglingInvite === key;
        return (
          <button
            type="button"
            className={`btn btn-sm ${row.can_invite ? "btn-success" : "btn-outline-secondary"}`}
            disabled={busy}
            onClick={(e) => {
              e.stopPropagation();
              handleToggleCanInvite(row);
            }}
          >
            {busy ? "…" : row.can_invite ? "Enabled" : "Disabled"}
          </button>
        );
      },
    },
  ];

  const inviteColumns = [
    {
      name: "Referred user",
      minWidth: "170px",
      cell: (row) => (
        <div className="invite-cell-left">
          <b>{row.hcc_id || "—"}</b>
          <div>{row.name || "—"}</div>
          <div className="invite-muted">{row.email || ""}</div>
        </div>
      ),
    },
    {
      name: "Mobile",
      selector: (row) => row.mobile_number || "—",
      width: "120px",
    },
    {
      name: "Joined",
      selector: (row) => row.joined_date,
      cell: (row) => formatDateTime(row.joined_date),
      minWidth: "140px",
    },
    {
      name: "Status",
      width: "120px",
      cell: (row) => (
        <span className={`invite-status-badge ${statusClass(row.status)}`}>
          {row.status || "—"}
        </span>
      ),
    },
    {
      name: "OTP",
      width: "90px",
      cell: (row) =>
        row.is_mobile_verified ? (
          <span className="invite-pill invite-pill--ok">Verified</span>
        ) : (
          <span className="invite-pill invite-pill--warn">Pending</span>
        ),
    },
    {
      name: "Pro",
      width: "80px",
      cell: (row) =>
        row.is_pro ? (
          <span className="invite-pill invite-pill--pro">PRO</span>
        ) : (
          "—"
        ),
    },
    {
      name: "Reward",
      selector: (row) => row.reward,
      cell: (row) => formatNum(row.reward),
      width: "90px",
    },
    {
      name: "Inviter",
      minWidth: "140px",
      cell: (row) => (
        <div className="invite-cell-left">
          <b>{row.inviter?.hcc_id || "—"}</b>
          <div className="invite-muted">{row.inviter?.name || ""}</div>
        </div>
      ),
    },
  ];

  return (
    <div className="child-sidenav">
      <div className="childnav-content invite-analytics-page">
        <div className="invite-toolbar">
          <div className="invite-toolbar-left">
            <h1 className="invite-title">Invite Analytics</h1>
            <span className="invite-range-pill">
              {startDate} → {endDate}
            </span>
          </div>
          <div className="invite-toolbar-right">
            <label className="invite-date-field">
              From
              <input
                type="date"
                className="form-control form-control-sm"
                value={startDate}
                max={endDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setInvitersPage(1);
                  setInvitesPage(1);
                }}
              />
            </label>
            <label className="invite-date-field">
              To
              <input
                type="date"
                className="form-control form-control-sm"
                value={endDate}
                min={startDate}
                max={defaultEnd()}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setInvitersPage(1);
                  setInvitesPage(1);
                }}
              />
            </label>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => {
                setStartDate(defaultStart());
                setEndDate(defaultEnd());
                setInvitersPage(1);
                setInvitesPage(1);
              }}
            >
              Last 30 days
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={refreshAll}
              title="Refresh"
            >
              <FiRefreshCw />
            </button>
          </div>
        </div>

        {overviewError ? (
          <div className="alert alert-danger">{overviewError}</div>
        ) : null}

        <div className="invite-kpi-grid">
          <SummaryCard
            label="Joined"
            value={summary.total_joined}
            hint="Registered with valid ref"
            icon={FiUsers}
            tone="brand"
          />
          <SummaryCard
            label="Unique"
            value={summary.unique_signups}
            hint="OTP verified · signup reward granted"
            icon={FiUserCheck}
            tone="sky"
          />
          <SummaryCard
            label="Duplicates"
            value={summary.duplicates}
            hint="OTP verified · email/mobile already existed"
            icon={FiCopy}
            tone="muted"
          />
          <SummaryCard
            label="Pending OTP"
            value={summary.pending_otp}
            hint="Joined · mobile not verified yet"
            icon={FiClock}
            tone="amber"
          />
          <SummaryCard
            label="Pro from invite"
            value={summary.pro_from_invite}
            hint="Referred users with is_pro"
            icon={FiStar}
            tone="green"
          />
          <SummaryCard
            label="Premium conversions"
            value={summary.premium_conversions}
            hint="First premium purchase reward"
            icon={FiAward}
            tone="violet"
          />
          <SummaryCard
            label="Credits paid"
            value={summary.credits_paid}
            hint="Sum of granted referral credits"
            icon={FiCreditCard}
            tone="brand"
          />
          <SummaryCard
            label="Active inviters"
            value={summary.inviters_active}
            hint={`Can invite enabled: ${formatNum(summary.can_invite_enabled)}`}
            icon={FiUserPlus}
            tone="sky"
          />
        </div>

        <div className="invite-chart-card">
          <div className="invite-section-title">Daily trend</div>
          {overviewLoading ? (
            <div className="invite-loader">Loading chart…</div>
          ) : (overview?.daily || []).length === 0 ? (
            <div className="invite-empty">No daily data for this range.</div>
          ) : (
            <div className="invite-chart-wrap">
              <Line data={chartData} options={chartOptions} />
            </div>
          )}
        </div>

        <div className="invite-tabs">
          <button
            type="button"
            className={`btn btn-sm ${activeTab === "inviters" ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => setActiveTab("inviters")}
          >
            Inviters
          </button>
          <button
            type="button"
            className={`btn btn-sm ${activeTab === "invites" ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => setActiveTab("invites")}
          >
            Invites
          </button>
        </div>

        {activeTab === "inviters" ? (
          <div className="invite-table-card">
            <form
              className="invite-filters"
              onSubmit={(e) => {
                e.preventDefault();
                setInviterSearch(inviterSearchInput.trim());
                setInvitersPage(1);
              }}
            >
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search inviter HCC / name / email"
                value={inviterSearchInput}
                onChange={(e) => setInviterSearchInput(e.target.value)}
              />
              <button type="submit" className="btn btn-sm btn-outline-secondary">
                Search
              </button>
              <span className="invite-count">{formatNum(invitersCount)} inviters</span>
            </form>
            <DataTable
              columns={inviterColumns}
              data={inviters}
              progressPending={invitersLoading}
              pagination
              paginationServer
              paginationTotalRows={invitersCount}
              paginationDefaultPage={invitersPage}
              paginationPerPage={invitersPageSize}
              paginationRowsPerPageOptions={[10, 20, 25, 50, 100]}
              onChangePage={setInvitersPage}
              onChangeRowsPerPage={(size) => {
                setInvitersPageSize(size);
                setInvitersPage(1);
              }}
              onRowClicked={openInvitesForInviter}
              pointerOnHover
              highlightOnHover
              customStyles={tableStyles}
              noDataComponent={<div className="invite-empty">No inviters found.</div>}
            />
            <p className="invite-hint">Click a row to view that inviter’s invites.</p>
          </div>
        ) : (
          <div className="invite-table-card">
            <form
              className="invite-filters"
              onSubmit={(e) => {
                e.preventDefault();
                setInviteSearch(inviteSearchInput.trim());
                setInvitesPage(1);
              }}
            >
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search referred / inviter HCC, name, email, mobile"
                value={inviteSearchInput}
                onChange={(e) => setInviteSearchInput(e.target.value)}
              />
              <select
                className="form-select form-select-sm"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setInvitesPage(1);
                }}
              >
                {STATUS_FILTERS.map((opt) => (
                  <option key={opt.value || "all"} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <select
                className="form-select form-select-sm"
                value={isProFilter}
                onChange={(e) => {
                  setIsProFilter(e.target.value);
                  setInvitesPage(1);
                }}
              >
                <option value="">Pro: all</option>
                <option value="true">Pro: yes</option>
                <option value="false">Pro: no</option>
              </select>
              <select
                className="form-select form-select-sm"
                value={otpFilter}
                onChange={(e) => {
                  setOtpFilter(e.target.value);
                  setInvitesPage(1);
                }}
              >
                <option value="">OTP: all</option>
                <option value="true">OTP verified</option>
                <option value="false">OTP pending</option>
              </select>
              <input
                type="text"
                className="form-control form-control-sm invite-inviter-filter"
                placeholder="Inviter HCC ID"
                value={inviterHccFilter}
                onChange={(e) => {
                  setInviterHccFilter(e.target.value.trim());
                  setInvitesPage(1);
                }}
              />
              <button type="submit" className="btn btn-sm btn-outline-secondary">
                Search
              </button>
              {(inviteSearch ||
                statusFilter ||
                isProFilter ||
                otpFilter ||
                inviterHccFilter) && (
                <button
                  type="button"
                  className="btn btn-sm btn-link"
                  onClick={() => {
                    setInviteSearchInput("");
                    setInviteSearch("");
                    setStatusFilter("");
                    setIsProFilter("");
                    setOtpFilter("");
                    setInviterHccFilter("");
                    setInvitesPage(1);
                  }}
                >
                  Clear
                </button>
              )}
              <span className="invite-count">{formatNum(invitesCount)} invites</span>
            </form>
            <DataTable
              columns={inviteColumns}
              data={invites}
              progressPending={invitesLoading}
              pagination
              paginationServer
              paginationTotalRows={invitesCount}
              paginationDefaultPage={invitesPage}
              paginationPerPage={invitesPageSize}
              paginationRowsPerPageOptions={[10, 20, 25, 50, 100]}
              onChangePage={setInvitesPage}
              onChangeRowsPerPage={(size) => {
                setInvitesPageSize(size);
                setInvitesPage(1);
              }}
              customStyles={tableStyles}
              noDataComponent={<div className="invite-empty">No invites found.</div>}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export { InviteAnalytics };
